import type { TeamTask } from "@codvero/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AgentAvatar } from "@/ui/AgentAvatar.tsx";
import { teamApi } from "@/api/studio.ts";
import { cn } from "@/lib/cn.ts";
import { Button } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

const tone: Record<TeamTask["status"], string> = { PENDING_APPROVAL: "border-warn/60 bg-warn-soft/40", QUEUED: "border-border border-dashed", RUNNING: "border-accent/60", DONE: "border-success/50", FAILED: "border-danger/50", REJECTED: "border-border opacity-70", CANCELLED: "border-border opacity-70" };

/** Approval card for a mentioned agent's task, rendered inline in the thread. */
export function TaskCard({ task, canEdit }: { task: TeamTask; canEdit: boolean }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const approve = useMutation({ mutationFn: () => teamApi.approve(task.id), onSuccess: () => qc.invalidateQueries({ queryKey: ["chats"] }) });
  const reject = useMutation({ mutationFn: () => teamApi.reject(task.id) });
  const err = approve.error ?? reject.error;
  return (
    <div className={cn("my-1 rounded-lg border px-3 py-2 text-sm", tone[task.status])}>
      <div className="flex items-center gap-2">
        <AgentAvatar avatar={task.agentAvatar} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2"><span className="font-medium">{task.agentName}</span><span dir="ltr" className="font-mono text-xs text-fg-muted">@{task.agentHandle}</span>{task.depth > 0 && task.kind !== "report" && <span className="rounded bg-surface-2 px-1 text-[10px] text-fg-muted">{t("team.handoff")}</span>}{task.kind === "report" && <span className="rounded bg-accent-soft px-1 text-[10px] text-accent">{t("team.report")}</span>}</div>
          <div className="text-xs text-fg-muted">{t(`team.status.${task.status}`)}{task.gates.length > 0 && task.status === "PENDING_APPROVAL" ? ` · ${t("team.gates", { gates: task.gates.map((g) => t(`studio.approvals.${g}`)).join("، ") })}` : ""}</div>
        </div>
        {task.status === "PENDING_APPROVAL" && canEdit && (
          <div className="flex gap-1">
            <Button size="sm" onClick={() => approve.mutate()} loading={approve.isPending}><Check className="size-3.5" /> {t("team.approve")}</Button>
            <Button size="sm" variant="ghost" onClick={() => reject.mutate()} loading={reject.isPending}><X className="size-3.5" /> {t("team.reject")}</Button>
          </div>
        )}
      </div>
      {task.depth > 0 && <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs text-fg-muted">{task.prompt}</p>}
      {err && <p className="mt-1 text-xs text-danger">{toMsg(err)}</p>}
    </div>
  );
}
